import React, { useContext, useEffect, useMemo, useState } from "react";
import {
  Building2,
  Eye,
  EyeOff,
  Plus,
  RefreshCcw,
  Save,
  Search,
  Store,
  X,
} from "lucide-react";
import { Navigate } from "react-router-dom";
import { toast } from "react-toastify";
import { GlobalContext } from "../../../Context/GlobalContext";
import LoadingScreen from "../../../utils/LoadingScreen";
import useBhandarewala from "../../../services/Order/UseBhandarewala";

const createInitialForm = (userID = "") => ({
  client_usercode: userID || "",
  restaurant_id: userID || "",
  vendor_name: "",
  business_name: "",
  mobile: "",
  password: "",
  alternate_mobile: "",
  email: "",
  vendor_type: "",
  gst_number: "",
  pan_number: "",
  fssai_number: "",
  contact_person: "",
  status: "Active",
  onboarding_status: "",
  created_by: userID || "",
});

const normalizeVendorList = (res) => {
  if (!res) return [];

  const candidates = [res?.response, res?.data, res?.result, res];

  for (const value of candidates) {
    if (Array.isArray(value)) {
      return value.filter(
        (item) => item && typeof item === "object" && !Array.isArray(item),
      );
    }

    if (value && typeof value === "object" && !Array.isArray(value)) {
      const nestedCandidates = [
        value?.vendors,
        value?.vendor_list,
        value?.vendorList,
        value?.data,
        value?.response,
        value?.result,
      ];

      const nestedList = nestedCandidates.find(Array.isArray);

      if (nestedList) {
        return nestedList.filter(
          (item) => item && typeof item === "object" && !Array.isArray(item),
        );
      }

      const looksLikeVendor =
        value?.vendor_name ||
        value?.vendorName ||
        value?.business_name ||
        value?.businessName ||
        value?.restaurant_id ||
        value?.restaurantId;

      if (looksLikeVendor) {
        return [value];
      }
    }
  }

  return [];
};

const getVendorField = (vendor, ...keys) => {
  for (const key of keys) {
    const value = vendor?.[key];

    if (value !== undefined && value !== null && String(value).trim() !== "") {
      return value;
    }
  }

  return "";
};

const mapVendorResponse = (vendor = {}) => ({
  ...vendor,
  client_usercode: getVendorField(vendor, "client_usercode", "clientUsercode"),
  restaurant_id: getVendorField(vendor, "restaurant_id", "restaurantId"),
  vendor_id: getVendorField(vendor, "vendor_id", "vendorId"),
  vendor_name: getVendorField(vendor, "vendor_name", "vendorName"),
  business_name: getVendorField(vendor, "business_name", "businessName"),
  mobile: getVendorField(vendor, "mobile", "vendor_mobile", "user_mobile"),
  alternate_mobile: getVendorField(
    vendor,
    "alternate_mobile",
    "alternateMobile",
  ),
  email: getVendorField(vendor, "email", "vendor_email", "user_email"),
  vendor_type: getVendorField(vendor, "vendor_type", "vendorType"),
  gst_number: getVendorField(vendor, "gst_number", "gstNumber"),
  pan_number: getVendorField(vendor, "pan_number", "panNumber"),
  fssai_number: getVendorField(vendor, "fssai_number", "fssaiNumber"),
  contact_person: getVendorField(vendor, "contact_person", "contactPerson"),
  status: getVendorField(vendor, "status", "vendor_status", "user_status"),
  onboarding_status: getVendorField(
    vendor,
    "onboarding_status",
    "onboardingStatus",
  ),
  created_by: getVendorField(vendor, "created_by", "createdBy"),
});

const isValidMobile = (value) =>
  /^[6-9]\d{9}$/.test(String(value || "").trim());

const isValidEmail = (value) =>
  /^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(String(value || "").trim());

const isValidPAN = (value) =>
  /^[A-Z]{5}[0-9]{4}[A-Z]$/.test(
    String(value || "")
      .trim()
      .toUpperCase(),
  );

const isValidGST = (value) =>
  /^[0-9]{2}[A-Z]{5}[0-9]{4}[A-Z][1-9A-Z]Z[0-9A-Z]$/.test(
    String(value || "")
      .trim()
      .toUpperCase(),
  );

const isValidFSSAI = (value) => /^\d{14}$/.test(String(value || "").trim());

const formatDisplayValue = (value) => {
  const text = String(value || "").trim();

  if (!text) return "-";

  return text
    .replace(/([a-z])([A-Z])/g, "$1 $2")
    .replace(/_/g, " ")
    .replace(/\s+/g, " ")
    .replace(/\b\w/g, (char) => char.toUpperCase());
};

const getNestedDetailList = (source, ...keys) => {
  for (const key of keys) {
    const value = source?.[key];

    if (Array.isArray(value)) {
      return value.filter((item) => {
        if (!item || typeof item !== "object" || Array.isArray(item)) {
          return false;
        }

        return Object.values(item).some(
          (fieldValue) =>
            fieldValue !== undefined &&
            fieldValue !== null &&
            String(fieldValue).trim() !== "",
        );
      });
    }
  }

  return [];
};

const VendorSection = () => {
  const { userID } = useContext(GlobalContext);

  const { getVendorDetails, registerVendorDetails } = useBhandarewala();

  const [activeTab, setActiveTab] = useState("list");
  const [form, setForm] = useState(() => createInitialForm(userID));
  const [vendors, setVendors] = useState([]);
  const [searchText, setSearchText] = useState("");
  const [vendorTypeFilter, setVendorTypeFilter] = useState("");
  const [statusFilter, setStatusFilter] = useState("");
  const [onboardingFilter, setOnboardingFilter] = useState("");
  const [loadingList, setLoadingList] = useState(false);
  const [saving, setSaving] = useState(false);
  const [showPassword, setShowPassword] = useState(false);
  const [editingIndex, setEditingIndex] = useState(null);
  const [selectedVendor, setSelectedVendor] = useState(null);
  const [pdfPreviewUrl, setPdfPreviewUrl] = useState("");

  const isAllowedUser = String(userID || "") === "A1437";

  useEffect(() => {
    if (!userID) return;

    setForm((prev) => ({
      ...prev,
      client_usercode: prev.client_usercode || userID,
      restaurant_id: prev.restaurant_id || userID,
      created_by: prev.created_by || userID,
    }));
  }, [userID]);

  const inputClass =
    "w-full rounded-lg border border-gray-300 px-3 py-2.5 text-sm outline-none transition focus:border-violet-500 focus:ring-2 focus:ring-violet-100";

  const labelClass = "mb-1.5 block text-sm font-medium text-gray-700";

  const fetchVendorList = async (showToast = true) => {
    if (!userID) {
      return [];
    }

    try {
      setLoadingList(true);

      const result = await getVendorDetails(userID);
      const data = result?.data;

      if (!result?.success) {
        setVendors([]);
        return [];
      }

      const list =
        Array.isArray(result?.rows) && result.rows.length > 0
          ? result.rows
          : normalizeVendorList(data);

      const mappedList = list.map(mapVendorResponse);

      setVendors(mappedList);

      if (showToast && mappedList.length === 0) {
        toast.info("No vendor details found");
      }

      return mappedList;
    } catch (error) {
      console.error(
        "GET VENDOR DETAILS ERROR:",
        error?.response?.data || error,
      );

      setVendors([]);

      if (showToast) {
        toast.error(
          error?.response?.data?.responseMessage ||
            error?.response?.data?.message ||
            "Failed to fetch vendor details",
        );
      }

      return [];
    } finally {
      setLoadingList(false);
    }
  };

  useEffect(() => {
    if (!isAllowedUser || !userID) {
      return;
    }

    fetchVendorList(false);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [isAllowedUser, userID]);

  const handleChange = (event) => {
    const { name, value } = event.target;

    setForm((prev) => ({
      ...prev,
      [name]: value,
    }));
  };

  const handleAddVendor = () => {
    setEditingIndex(null);
    setShowPassword(false);
    setForm(createInitialForm(userID));
    setActiveTab("form");
  };

  const handleRegisterVendor = async (event) => {
    event.preventDefault();

    const payload = {
      client_usercode: String(form.client_usercode || userID || "").trim(),
      restaurant_id: String(form.restaurant_id || userID || "").trim(),
      vendor_name: String(form.vendor_name || "").trim(),
      business_name: String(form.business_name || "").trim(),
      mobile: String(form.mobile || "").trim(),
      password: String(form.password || ""),
      alternate_mobile: String(form.alternate_mobile || "").trim(),
      email: String(form.email || "").trim(),
      vendor_type: String(form.vendor_type || "").trim(),
      gst_number: String(form.gst_number || "")
        .trim()
        .toUpperCase(),
      pan_number: String(form.pan_number || "")
        .trim()
        .toUpperCase(),
      fssai_number: String(form.fssai_number || "").trim(),
      contact_person: String(form.contact_person || "").trim(),
      status: String(form.status || "").trim(),
      onboarding_status: String(form.onboarding_status || "").trim(),
      created_by: String(form.created_by || userID || "").trim(),
    };

    if (!payload.vendor_name) {
      toast.error("Vendor name is required");
      return;
    }

    if (!payload.business_name) {
      toast.error("Business name is required");
      return;
    }

    if (!payload.mobile) {
      toast.error("Mobile number is required");
      return;
    }

    if (editingIndex === null && !payload.password) {
      toast.error("Password is required");
      return;
    }

    if (payload.password && payload.password.length < 6) {
      toast.error("Password must be at least 6 characters");
      return;
    }

    if (!isValidMobile(payload.mobile)) {
      toast.error(
        "Mobile number must be a valid 10 digit Indian mobile number",
      );
      return;
    }

    if (payload.alternate_mobile && !isValidMobile(payload.alternate_mobile)) {
      toast.error(
        "Alternate mobile number must be a valid 10 digit Indian mobile number",
      );
      return;
    }

    if (payload.email && !isValidEmail(payload.email)) {
      toast.error("Please enter a valid email address");
      return;
    }

    if (payload.pan_number && !isValidPAN(payload.pan_number)) {
      toast.error("PAN number must be valid, for example ABCDE1234F");
      return;
    }

    if (payload.gst_number && !isValidGST(payload.gst_number)) {
      toast.error("GST number must be a valid 15 character GSTIN");
      return;
    }

    if (payload.fssai_number && !isValidFSSAI(payload.fssai_number)) {
      toast.error("FSSAI number must contain exactly 14 digits");
      return;
    }

    try {
      setSaving(true);

      const result = await registerVendorDetails(payload);
      const data = result?.data;

      if (!result?.success) {
        return;
      }

      toast.success(
        data?.responseMessage ||
          (editingIndex !== null
            ? "Vendor updated successfully"
            : "Vendor saved successfully"),
      );

      await fetchVendorList(false);

      setForm(createInitialForm(userID));
      setEditingIndex(null);
      setShowPassword(false);
      setActiveTab("list");
    } catch (error) {
      console.error("REGISTER VENDOR ERROR:", error?.response?.data || error);

      toast.error(
        error?.response?.data?.responseMessage ||
          error?.response?.data?.message ||
          "Vendor registration failed",
      );
    } finally {
      setSaving(false);
    }
  };

  const filteredVendors = useMemo(() => {
    const search = searchText.trim().toLowerCase();
    const selectedVendorType = vendorTypeFilter.trim().toLowerCase();
    const selectedStatus = statusFilter.trim().toLowerCase();
    const selectedOnboarding = onboardingFilter.trim().toLowerCase();

    const sorted = [...vendors].sort((a, b) => {
      const aName = String(
        getVendorField(a, "vendor_name", "vendorName"),
      ).toLowerCase();

      const bName = String(
        getVendorField(b, "vendor_name", "vendorName"),
      ).toLowerCase();

      return aName.localeCompare(bName);
    });

    return sorted.filter((vendor) => {
      const vendorType = String(
        getVendorField(vendor, "vendor_type", "vendorType"),
      )
        .trim()
        .toLowerCase();

      const vendorStatus = String(
        getVendorField(vendor, "vendor_status", "status", "user_status"),
      )
        .trim()
        .toLowerCase();

      const onboardingStatus = String(
        getVendorField(vendor, "onboarding_status", "onboardingStatus"),
      )
        .trim()
        .toLowerCase();

      if (selectedVendorType && vendorType !== selectedVendorType) {
        return false;
      }

      if (selectedStatus && vendorStatus !== selectedStatus) {
        return false;
      }

      if (selectedOnboarding && onboardingStatus !== selectedOnboarding) {
        return false;
      }

      if (!search) {
        return true;
      }

      const searchableValues = [
        getVendorField(vendor, "vendor_id", "vendorId"),
        getVendorField(vendor, "vendor_name", "vendorName"),
        getVendorField(vendor, "business_name", "businessName"),
        getVendorField(vendor, "mobile", "vendor_mobile", "user_mobile"),
        getVendorField(vendor, "alternate_mobile", "alternateMobile"),
        getVendorField(vendor, "email", "vendor_email", "user_email"),
        getVendorField(vendor, "vendor_type", "vendorType"),
        getVendorField(vendor, "gst_number", "gstNumber"),
        getVendorField(vendor, "pan_number", "panNumber"),
        getVendorField(vendor, "fssai_number", "fssaiNumber"),
        getVendorField(vendor, "contact_person", "contactPerson"),
        getVendorField(vendor, "vendor_status", "user_status", "status"),
        getVendorField(vendor, "onboarding_status", "onboardingStatus"),
      ];

      return searchableValues.some((value) =>
        String(value || "")
          .toLowerCase()
          .includes(search),
      );
    });
  }, [vendors, searchText, vendorTypeFilter, statusFilter, onboardingFilter]);

  if (!isAllowedUser) {
    return <Navigate to="/orders" replace />;
  }

  return (
    <div className="h-full min-h-0 overflow-hidden p-6 montserrat-font-1">
      <div className="flex h-full max-w-[1900px] flex-col">
        <div className="mb-5 flex flex-shrink-0 items-center justify-between">
          <div>
            <div className="flex items-center gap-3">
              <div className="flex h-11 w-11 items-center justify-center rounded-xl bg-violet-100">
                <Store className="h-5 w-5 text-violet-600" />
              </div>

              <div>
                <h1 className="text-2xl font-semibold text-gray-900">
                  Vendor Management
                </h1>
                <p className="mt-1 text-sm text-gray-500">
                  Manage restaurant vendors
                </p>
              </div>
            </div>
          </div>

          {activeTab === "list" && (
            <button
              type="button"
              onClick={handleAddVendor}
              className="flex items-center gap-2 rounded-lg bg-violet-600 px-4 py-2.5 text-sm font-semibold text-white transition hover:bg-violet-700"
            >
              <Plus size={17} />
              Add Vendor
            </button>
          )}
        </div>

        <div className="flex min-h-0 flex-1 flex-col overflow-hidden rounded-2xl border border-gray-200 bg-white shadow-sm">
          <div className="flex flex-shrink-0 items-center border-b border-gray-200 px-6">
            <button
              type="button"
              onClick={() => setActiveTab("list")}
              className={`border-b-2 px-4 py-3 text-sm font-semibold transition ${
                activeTab === "list"
                  ? "border-violet-600 text-violet-600"
                  : "border-transparent text-gray-500 hover:text-gray-800"
              }`}
            >
              Vendor List
              {vendors.length > 0 && (
                <span className="ml-2 rounded-full bg-violet-100 px-2 py-0.5 text-xs text-violet-700">
                  {vendors.length}
                </span>
              )}
            </button>

            <button
              type="button"
              onClick={handleAddVendor}
              className={`border-b-2 px-4 py-3 text-sm font-semibold transition ${
                activeTab === "form"
                  ? "border-violet-600 text-violet-600"
                  : "border-transparent text-gray-500 hover:text-gray-800"
              }`}
            >
              {editingIndex !== null ? "Edit Vendor" : "Add Vendor"}
            </button>
          </div>

          <div className="min-h-0 flex-1 overflow-y-auto p-6">
            {activeTab === "list" && (
              <div className="flex min-h-0 flex-col">
                <div className="mb-5 flex flex-shrink-0 flex-wrap items-center gap-3">
                  <div className="relative min-w-[260px] flex-1">
                    <Search
                      size={17}
                      className="absolute left-3 top-1/2 -translate-y-1/2 text-gray-400"
                    />

                    <input
                      type="text"
                      value={searchText}
                      onChange={(event) => setSearchText(event.target.value)}
                      placeholder="Search vendor name, business, mobile, email, GST, PAN..."
                      className="w-full rounded-lg border border-gray-300 py-2.5 pl-10 pr-4 text-sm outline-none transition focus:border-violet-500 focus:ring-2 focus:ring-violet-100"
                    />
                  </div>

                  <select
                    value={vendorTypeFilter}
                    onChange={(event) =>
                      setVendorTypeFilter(event.target.value)
                    }
                    className="min-w-[150px] rounded-lg border border-gray-300 bg-white px-3 py-2.5 text-sm text-gray-700 outline-none transition focus:border-violet-500 focus:ring-2 focus:ring-violet-100"
                  >
                    <option value="">All Types</option>
                    <option value="Owner">Owner</option>
                    <option value="Manager">Manager</option>
                    <option value="Staff">Staff</option>
                    <option value="Kitchen">Kitchen</option>
                  </select>

                  <select
                    value={statusFilter}
                    onChange={(event) => setStatusFilter(event.target.value)}
                    className="min-w-[150px] rounded-lg border border-gray-300 bg-white px-3 py-2.5 text-sm text-gray-700 outline-none transition focus:border-violet-500 focus:ring-2 focus:ring-violet-100"
                  >
                    <option value="">All Status</option>
                    <option value="Active">Active</option>
                    <option value="Inactive">Inactive</option>
                    <option value="Suspended">Suspended</option>
                    <option value="Rejected">Rejected</option>
                  </select>

                  <select
                    value={onboardingFilter}
                    onChange={(event) =>
                      setOnboardingFilter(event.target.value)
                    }
                    className="min-w-[190px] rounded-lg border border-gray-300 bg-white px-3 py-2.5 text-sm text-gray-700 outline-none transition focus:border-violet-500 focus:ring-2 focus:ring-violet-100"
                  >
                    <option value="">All Onboarding</option>
                    <option value="Invited">Invited</option>
                    <option value="profilePending">Profile Pending</option>
                    <option value="DocumentsPending">Documents Pending</option>
                    <option value="ApprovalPending">Approval Pending</option>
                    <option value="Completed">Completed</option>
                  </select>

                  <button
                    type="button"
                    onClick={() => fetchVendorList()}
                    disabled={loadingList}
                    className="flex items-center justify-center gap-2 rounded-lg border border-gray-300 bg-white px-4 py-2.5 text-sm font-semibold text-gray-700 transition hover:bg-gray-50 disabled:cursor-not-allowed disabled:opacity-60"
                  >
                    <RefreshCcw
                      className={`h-4 w-4 ${loadingList ? "animate-spin" : ""}`}
                    />
                    Refresh
                  </button>
                </div>

                {loadingList ? (
                  <div className="flex min-h-[300px] items-center justify-center">
                    <LoadingScreen text="Loading vendors..." />
                  </div>
                ) : filteredVendors.length > 0 ? (
                  <div className="overflow-hidden rounded-xl border border-gray-200">
                    <div className="max-h-[calc(100vh-330px)] overflow-auto">
                      <table className="min-w-[1050px] w-full divide-y divide-gray-200 text-sm">
                        <thead className="sticky top-0 z-10 bg-violet-300">
                          <tr>
                            <th className="px-4 py-3 text-left font-semibold text-gray-900">
                              Vendor
                            </th>
                            <th className="px-4 py-3 text-left font-semibold text-gray-900">
                              Business
                            </th>
                            <th className="px-4 py-3 text-left font-semibold text-gray-900">
                              Mobile
                            </th>
                            <th className="px-4 py-3 text-left font-semibold text-gray-900">
                              Email
                            </th>
                            <th className="px-4 py-3 text-left font-semibold text-gray-900">
                              Type
                            </th>
                            <th className="px-4 py-3 text-left font-semibold text-gray-900">
                              GST
                            </th>
                            <th className="px-4 py-3 text-left font-semibold text-gray-900">
                              Status
                            </th>
                            <th className="px-4 py-3 text-left font-semibold text-gray-900">
                              Onboarding
                            </th>
                            <th className="px-4 py-3 text-right font-semibold text-gray-900">
                              Details
                            </th>
                          </tr>
                        </thead>

                        <tbody className="divide-y divide-gray-100 bg-white">
                          {filteredVendors.map((vendor, index) => {
                            const vendorName =
                              getVendorField(
                                vendor,
                                "vendor_name",
                                "vendorName",
                              ) || "-";

                            const businessName =
                              getVendorField(
                                vendor,
                                "business_name",
                                "businessName",
                              ) || "-";

                            const status = formatDisplayValue(
                              getVendorField(
                                vendor,
                                "status",
                                "vendor_status",
                                "user_status",
                              ),
                            );

                            const onboardingStatus = formatDisplayValue(
                              getVendorField(
                                vendor,
                                "onboarding_status",
                                "onboardingStatus",
                              ),
                            );

                            return (
                              <tr
                                key={
                                  vendor?.vendor_id ||
                                  vendor?.vendorId ||
                                  `${vendorName}-${index}`
                                }
                                className="transition hover:bg-gray-50"
                              >
                                <td className="px-4 py-3">
                                  <div className="flex items-center gap-3">
                                    <div className="flex h-9 w-9 shrink-0 items-center justify-center rounded-lg bg-violet-100">
                                      <Building2 className="h-4 w-4 text-violet-600" />
                                    </div>

                                    <div>
                                      <p className="font-semibold text-gray-900">
                                        {vendorName}
                                      </p>
                                      <p className="text-xs text-gray-400">
                                        ID:{" "}
                                        {getVendorField(
                                          vendor,
                                          "vendor_id",
                                          "vendorId",
                                        ) || "-"}
                                      </p>
                                    </div>
                                  </div>
                                </td>

                                <td className="px-4 py-3 text-gray-700">
                                  {businessName}
                                </td>

                                <td className="px-4 py-3 text-gray-700">
                                  {getVendorField(
                                    vendor,
                                    "mobile",
                                    "vendor_mobile",
                                    "user_mobile",
                                  ) || "-"}
                                </td>

                                <td className="px-4 py-3 text-gray-700">
                                  {getVendorField(
                                    vendor,
                                    "email",
                                    "vendor_email",
                                    "user_email",
                                  ) || "-"}
                                </td>

                                <td className="px-4 py-3 text-gray-700">
                                  {getVendorField(
                                    vendor,
                                    "vendor_type",
                                    "vendorType",
                                  ) || "-"}
                                </td>

                                <td className="px-4 py-3 text-gray-700">
                                  {getVendorField(
                                    vendor,
                                    "gst_number",
                                    "gstNumber",
                                  ) || "-"}
                                </td>

                                <td className="px-4 py-3">
                                  <span
                                    className={`inline-flex rounded-full px-2.5 py-1 text-xs font-semibold ${
                                      status === "Active"
                                        ? "bg-green-100 text-green-700"
                                        : status === "Suspended"
                                          ? "bg-amber-100 text-amber-700"
                                          : status === "Inactive" ||
                                              status === "Rejected"
                                            ? "bg-red-100 text-red-700"
                                            : "bg-gray-100 text-gray-700"
                                    }`}
                                  >
                                    {status}
                                  </span>
                                </td>

                                <td className="px-4 py-3 text-gray-700">
                                  {onboardingStatus}
                                </td>

                                <td className="px-4 py-3 text-right">
                                  <button
                                    type="button"
                                    onClick={() => setSelectedVendor(vendor)}
                                    title="View vendor details"
                                    className="inline-flex h-8 w-8 items-center justify-center rounded-lg border border-gray-300 text-gray-600 transition hover:border-violet-300 hover:bg-violet-50 hover:text-violet-700"
                                  >
                                    <Eye size={15} />
                                  </button>
                                </td>
                              </tr>
                            );
                          })}
                        </tbody>
                      </table>
                    </div>
                  </div>
                ) : (
                  <div className="flex min-h-[280px] items-center justify-center rounded-xl border border-dashed border-gray-300 bg-gray-50">
                    <div className="text-center">
                      <Store className="mx-auto h-10 w-10 text-gray-300" />

                      <p className="mt-3 text-sm font-semibold text-gray-700">
                        {searchText
                          ? "No matching vendors found"
                          : "No vendors found"}
                      </p>

                      <p className="mt-1 text-xs text-gray-400">
                        {searchText
                          ? "Try another search term."
                          : "Add your first vendor."}
                      </p>
                    </div>
                  </div>
                )}
              </div>
            )}

            {activeTab === "form" && (
              <form onSubmit={handleRegisterVendor}>
                <div className="mb-5 rounded-xl border border-violet-100 bg-violet-50 px-4 py-3">
                  <p className="text-sm font-semibold text-violet-900">
                    {editingIndex !== null ? "Edit Vendor" : "Add New Vendor"}
                  </p>
                  <p className="mt-0.5 text-xs text-violet-700">
                    {editingIndex !== null
                      ? "Update the selected vendor details."
                      : "Create a new vendor for this restaurant."}
                  </p>
                </div>

                <div className="grid grid-cols-1 gap-5 md:grid-cols-2">
                  <div>
                    <label className={labelClass}>Client Usercode</label>
                    <input
                      name="client_usercode"
                      value={form.client_usercode}
                      onChange={handleChange}
                      className={inputClass}
                    />
                  </div>

                  <div>
                    <label className={labelClass}>Restaurant ID</label>
                    <input
                      name="restaurant_id"
                      value={form.restaurant_id}
                      readOnly
                      className={`${inputClass} bg-gray-50 text-gray-500`}
                    />
                  </div>

                  <div>
                    <label className={labelClass}>
                      Vendor Name <span className="text-red-500">*</span>
                    </label>
                    <input
                      name="vendor_name"
                      value={form.vendor_name}
                      onChange={handleChange}
                      placeholder="Enter vendor name"
                      className={inputClass}
                    />
                  </div>

                  <div>
                    <label className={labelClass}>
                      Business Name <span className="text-red-500">*</span>
                    </label>
                    <input
                      name="business_name"
                      value={form.business_name}
                      onChange={handleChange}
                      placeholder="Enter business name"
                      className={inputClass}
                    />
                  </div>

                  <div>
                    <label className={labelClass}>
                      Mobile <span className="text-red-500">*</span>
                    </label>
                    <input
                      name="mobile"
                      value={form.mobile}
                      onChange={(e) => {
                        const value = e.target.value
                          .replace(/\D/g, "")
                          .slice(0, 10);

                        setForm((prev) => ({
                          ...prev,
                          mobile: value,
                        }));
                      }}
                      inputMode="numeric"
                      maxLength={10}
                      placeholder="10 digit mobile number"
                      className={inputClass}
                    />
                  </div>

                  <div>
                    <label className={labelClass}>
                      Password{" "}
                      {editingIndex === null && (
                        <span className="text-red-500">*</span>
                      )}
                    </label>
                    <div className="relative">
                      <input
                        type={showPassword ? "text" : "password"}
                        name="password"
                        value={form.password}
                        onChange={handleChange}
                        minLength={6}
                        autoComplete="new-password"
                        placeholder={
                          editingIndex !== null
                            ? "Leave blank to keep existing password"
                            : "Minimum 6 characters"
                        }
                        className={`${inputClass} pr-11`}
                      />

                      <button
                        type="button"
                        onClick={() => setShowPassword((prev) => !prev)}
                        className="absolute right-3 top-1/2 -translate-y-1/2 text-gray-400 transition hover:text-gray-700"
                        title={showPassword ? "Hide password" : "Show password"}
                        aria-label={
                          showPassword ? "Hide password" : "Show password"
                        }
                      >
                        {showPassword ? (
                          <EyeOff size={17} />
                        ) : (
                          <Eye size={17} />
                        )}
                      </button>
                    </div>
                  </div>

                  <div>
                    <label className={labelClass}>Alternate Mobile</label>
                    <input
                      name="alternate_mobile"
                      value={form.alternate_mobile}
                      onChange={(e) => {
                        const value = e.target.value
                          .replace(/\D/g, "")
                          .slice(0, 10);

                        setForm((prev) => ({
                          ...prev,
                          alternate_mobile: value,
                        }));
                      }}
                      inputMode="numeric"
                      maxLength={10}
                      placeholder="10 digit alternate mobile"
                      className={inputClass}
                    />
                  </div>

                  <div>
                    <label className={labelClass}>Email</label>
                    <input
                      type="email"
                      name="email"
                      value={form.email}
                      onChange={handleChange}
                      placeholder="example@email.com"
                      className={inputClass}
                    />
                  </div>

                  <div>
                    <label className={labelClass}>Vendor Type</label>
                    <select
                      name="vendor_type"
                      value={form.vendor_type}
                      onChange={handleChange}
                      className={inputClass}
                    >
                      <option value="">Select Vendor Type</option>
                      <option value="Owner">Owner</option>
                      <option value="Manager">Manager</option>
                      <option value="Staff">Staff</option>
                      <option value="Kitchen">Kitchen</option>
                    </select>
                  </div>

                  <div>
                    <label className={labelClass}>GST Number</label>
                    <input
                      name="gst_number"
                      value={form.gst_number}
                      onChange={(e) =>
                        setForm((prev) => ({
                          ...prev,
                          gst_number: e.target.value
                            .toUpperCase()
                            .replace(/[^A-Z0-9]/g, "")
                            .slice(0, 15),
                        }))
                      }
                      maxLength={15}
                      placeholder="15 character GSTIN"
                      className={inputClass}
                    />
                  </div>

                  <div>
                    <label className={labelClass}>PAN Number</label>
                    <input
                      name="pan_number"
                      value={form.pan_number}
                      onChange={(e) =>
                        setForm((prev) => ({
                          ...prev,
                          pan_number: e.target.value
                            .toUpperCase()
                            .replace(/[^A-Z0-9]/g, "")
                            .slice(0, 10),
                        }))
                      }
                      maxLength={10}
                      placeholder="ABCDE1234F"
                      className={inputClass}
                    />
                  </div>

                  <div>
                    <label className={labelClass}>FSSAI Number</label>
                    <input
                      name="fssai_number"
                      value={form.fssai_number}
                      onChange={(e) => {
                        const value = e.target.value
                          .replace(/\D/g, "")
                          .slice(0, 14);

                        setForm((prev) => ({
                          ...prev,
                          fssai_number: value,
                        }));
                      }}
                      inputMode="numeric"
                      maxLength={14}
                      placeholder="14 digit FSSAI number"
                      className={inputClass}
                    />
                  </div>

                  <div>
                    <label className={labelClass}>Contact Person</label>
                    <input
                      name="contact_person"
                      value={form.contact_person}
                      onChange={handleChange}
                      placeholder="Enter contact person"
                      className={inputClass}
                    />
                  </div>

                  <div>
                    <label className={labelClass}>Status</label>
                    <select
                      name="status"
                      value={form.status}
                      onChange={handleChange}
                      className={inputClass}
                    >
                      <option value="Active">Active</option>
                      <option value="Inactive">Inactive</option>
                      <option value="Suspended">Suspended</option>
                      <option value="Rejected">Rejected</option>
                    </select>
                  </div>

                  <div>
                    <label className={labelClass}>Onboarding Status</label>
                    <select
                      name="onboarding_status"
                      value={form.onboarding_status}
                      onChange={handleChange}
                      className={inputClass}
                    >
                      <option value="">Select Onboarding Status</option>
                      <option value="Invited">Invited</option>
                      <option value="profilePending">Profile Pending</option>
                      <option value="DocumentsPending">
                        Documents Pending
                      </option>
                      <option value="ApprovalPending">Approval Pending</option>
                      <option value="Completed">Completed</option>
                    </select>
                  </div>

                  <div className="md:col-span-2">
                    <label className={labelClass}>Created By</label>
                    <input
                      name="created_by"
                      value={form.created_by}
                      onChange={handleChange}
                      className={inputClass}
                    />
                  </div>
                </div>

                <div className="mt-6 flex items-center justify-end gap-3 border-t border-gray-200 pt-5">
                  <button
                    type="button"
                    onClick={() => {
                      setEditingIndex(null);
                      setShowPassword(false);
                      setForm(createInitialForm(userID));
                      setActiveTab("list");
                    }}
                    className="rounded-lg border border-gray-300 px-4 py-2.5 text-sm font-semibold text-gray-700 transition hover:bg-gray-50"
                  >
                    Cancel
                  </button>

                  <button
                    type="submit"
                    disabled={saving}
                    className="flex items-center gap-2 rounded-lg bg-violet-600 px-5 py-2.5 text-sm font-semibold text-white transition hover:bg-violet-700 disabled:cursor-not-allowed disabled:opacity-60"
                  >
                    {saving ? (
                      <RefreshCcw className="h-4 w-4 animate-spin" />
                    ) : (
                      <Save className="h-4 w-4" />
                    )}

                    {saving
                      ? "Saving..."
                      : editingIndex !== null
                        ? "Update Vendor"
                        : "Save Vendor"}
                  </button>
                </div>
              </form>
            )}
          </div>
        </div>
      </div>

      {selectedVendor && !pdfPreviewUrl && (
        <div
          className="fixed inset-0 z-[120] flex items-center justify-center bg-black/45 p-4"
          onClick={() => setSelectedVendor(null)}
        >
          <div
            className="flex max-h-[90vh] w-full max-w-5xl flex-col overflow-hidden rounded-2xl bg-white shadow-2xl"
            onClick={(event) => event.stopPropagation()}
          >
            <div className="flex items-center justify-between border-b border-gray-200 px-6 py-4">
              <div>
                <h2 className="text-lg font-semibold text-gray-900">
                  Vendor Details
                </h2>
                <p className="mt-1 text-xs text-gray-500">
                  {getVendorField(
                    selectedVendor,
                    "vendor_name",
                    "vendorName",
                  ) || "-"}
                  {" • "}
                  {getVendorField(selectedVendor, "vendor_id", "vendorId") ||
                    "-"}
                </p>
              </div>

              <button
                type="button"
                onClick={() => setSelectedVendor(null)}
                className="rounded-lg p-2 text-gray-400 transition hover:bg-gray-100 hover:text-gray-700"
              >
                <X size={20} />
              </button>
            </div>

            <div className="flex-1 overflow-y-auto p-6">
              <div className="grid grid-cols-1 gap-4 sm:grid-cols-2 lg:grid-cols-3">
                {[
                  [
                    "Vendor ID",
                    getVendorField(selectedVendor, "vendor_id", "vendorId"),
                  ],
                  [
                    "Client Usercode",
                    getVendorField(
                      selectedVendor,
                      "client_usercode",
                      "clientUsercode",
                    ),
                  ],
                  [
                    "Restaurant ID",
                    getVendorField(
                      selectedVendor,
                      "restaurant_id",
                      "restaurantId",
                    ),
                  ],
                  [
                    "Vendor Name",
                    getVendorField(selectedVendor, "vendor_name", "vendorName"),
                  ],
                  [
                    "Business Name",
                    getVendorField(
                      selectedVendor,
                      "business_name",
                      "businessName",
                    ),
                  ],
                  [
                    "Vendor Mobile",
                    getVendorField(
                      selectedVendor,
                      "mobile",
                      "vendor_mobile",
                      "user_mobile",
                    ),
                  ],
                  [
                    "Alternate Mobile",
                    getVendorField(
                      selectedVendor,
                      "alternate_mobile",
                      "alternateMobile",
                    ),
                  ],
                  [
                    "Vendor Email",
                    getVendorField(
                      selectedVendor,
                      "email",
                      "vendor_email",
                      "user_email",
                    ),
                  ],
                  [
                    "Vendor Type",
                    getVendorField(selectedVendor, "vendor_type", "vendorType"),
                  ],
                  [
                    "GST Number",
                    getVendorField(selectedVendor, "gst_number", "gstNumber"),
                  ],
                  [
                    "PAN Number",
                    getVendorField(selectedVendor, "pan_number", "panNumber"),
                  ],
                  [
                    "FSSAI Number",
                    getVendorField(
                      selectedVendor,
                      "fssai_number",
                      "fssaiNumber",
                    ),
                  ],
                  [
                    "Contact Person",
                    getVendorField(
                      selectedVendor,
                      "contact_person",
                      "contactPerson",
                    ),
                  ],
                  [
                    "Vendor Status",
                    formatDisplayValue(
                      getVendorField(
                        selectedVendor,
                        "status",
                        "vendor_status",
                        "user_status",
                      ),
                    ),
                  ],
                  [
                    "Onboarding Status",
                    formatDisplayValue(
                      getVendorField(
                        selectedVendor,
                        "onboarding_status",
                        "onboardingStatus",
                      ),
                    ),
                  ],
                  [
                    "Onboarding Date",
                    getVendorField(
                      selectedVendor,
                      "onboarding_date",
                      "onboardingDate",
                    ),
                  ],
                  [
                    "Created By",
                    getVendorField(selectedVendor, "created_by", "createdBy"),
                  ],
                  [
                    "Created At",
                    getVendorField(selectedVendor, "created_at", "createdAt"),
                  ],
                  ["Username", getVendorField(selectedVendor, "username")],
                  [
                    "User Email",
                    getVendorField(selectedVendor, "user_email", "userEmail"),
                  ],
                  [
                    "User Mobile",
                    getVendorField(selectedVendor, "user_mobile", "userMobile"),
                  ],
                  ["Role", getVendorField(selectedVendor, "role")],
                  [
                    "User Status",
                    formatDisplayValue(
                      getVendorField(
                        selectedVendor,
                        "user_status",
                        "userStatus",
                      ),
                    ),
                  ],
                ].map(([label, value]) => (
                  <div
                    key={label}
                    className="rounded-xl border border-gray-200 bg-gray-50 p-4"
                  >
                    <p className="text-xs font-medium uppercase tracking-wide text-gray-400">
                      {label}
                    </p>
                    <p className="mt-1 break-words text-sm font-semibold text-gray-800">
                      {value || "-"}
                    </p>
                  </div>
                ))}

                <div className="sm:col-span-2 lg:col-span-3">
                  <div className="mb-3 flex items-center justify-between">
                    <h3 className="text-sm font-semibold text-gray-900">
                      Bank Details
                    </h3>
                    <span className="rounded-full bg-violet-100 px-2.5 py-1 text-xs font-semibold text-violet-700">
                      {
                        getNestedDetailList(
                          selectedVendor,
                          "bankDetails",
                          "bank_details",
                        ).length
                      }
                    </span>
                  </div>

                  {getNestedDetailList(
                    selectedVendor,
                    "bankDetails",
                    "bank_details",
                  ).length > 0 ? (
                    <div className="space-y-4">
                      {getNestedDetailList(
                        selectedVendor,
                        "bankDetails",
                        "bank_details",
                      ).map((bank, bankIndex) => (
                        <div
                          key={
                            getVendorField(bank, "pk_bank_id", "pkBankId") ||
                            `bank-${bankIndex}`
                          }
                          className="rounded-xl border border-gray-200 bg-white p-4"
                        >
                          <p className="mb-3 text-xs font-semibold uppercase tracking-wide text-violet-600">
                            Bank #{bankIndex + 1}
                          </p>

                          <div className="grid grid-cols-1 gap-3 sm:grid-cols-2 lg:grid-cols-4">
                            {[
                              [
                                "Bank ID",
                                getVendorField(bank, "pk_bank_id", "pkBankId"),
                              ],
                              [
                                "Account Holder Name",
                                getVendorField(
                                  bank,
                                  "account_holder_name",
                                  "accountHolderName",
                                ),
                              ],
                              [
                                "Bank Name",
                                getVendorField(bank, "bank_name", "bankName"),
                              ],
                              [
                                "Account Number",
                                getVendorField(
                                  bank,
                                  "account_number",
                                  "accountNumber",
                                ),
                              ],
                              [
                                "IFSC Code",
                                getVendorField(bank, "ifsc_code", "ifscCode"),
                              ],
                              [
                                "Branch Name",
                                getVendorField(
                                  bank,
                                  "branch_name",
                                  "branchName",
                                ),
                              ],
                              [
                                "UPI ID",
                                getVendorField(bank, "upi_id", "upiId"),
                              ],
                              [
                                "Verification Status",
                                formatDisplayValue(
                                  getVendorField(
                                    bank,
                                    "bank_verification_status",
                                    "bankVerificationStatus",
                                  ),
                                ),
                              ],
                            ].map(([label, value]) => (
                              <div
                                key={`${bankIndex}-${label}`}
                                className="rounded-lg bg-gray-50 p-3"
                              >
                                <p className="text-[11px] font-medium uppercase tracking-wide text-gray-400">
                                  {label}
                                </p>
                                <p className="mt-1 break-words text-sm font-semibold text-gray-800">
                                  {value || "-"}
                                </p>
                              </div>
                            ))}
                          </div>
                        </div>
                      ))}
                    </div>
                  ) : (
                    <div className="rounded-xl border border-dashed border-gray-300 bg-gray-50 p-4 text-sm font-medium text-gray-500">
                      No bank details available.
                    </div>
                  )}
                </div>

                <div className="sm:col-span-2 lg:col-span-3">
                  <div className="mb-3 flex items-center justify-between">
                    <h3 className="text-sm font-semibold text-gray-900">
                      Document Details
                    </h3>
                    <span className="rounded-full bg-violet-100 px-2.5 py-1 text-xs font-semibold text-violet-700">
                      {
                        getNestedDetailList(
                          selectedVendor,
                          "documentDetails",
                          "document_details",
                        ).length
                      }
                    </span>
                  </div>

                  {getNestedDetailList(
                    selectedVendor,
                    "documentDetails",
                    "document_details",
                  ).length > 0 ? (
                    <div className="space-y-4">
                      {getNestedDetailList(
                        selectedVendor,
                        "documentDetails",
                        "document_details",
                      ).map((document, documentIndex) => {
                        const documentUrl = getVendorField(
                          document,
                          "document_url",
                          "documentUrl",
                        );

                        return (
                          <div
                            key={
                              getVendorField(
                                document,
                                "pk_document_id",
                                "pkDocumentId",
                              ) || `document-${documentIndex}`
                            }
                            className="rounded-xl border border-gray-200 bg-white p-4"
                          >
                            <p className="mb-3 text-xs font-semibold uppercase tracking-wide text-violet-600">
                              Document #{documentIndex + 1}
                            </p>

                            <div className="grid grid-cols-1 gap-3 sm:grid-cols-2 lg:grid-cols-3">
                              {[
                                [
                                  "Document ID",
                                  getVendorField(
                                    document,
                                    "pk_document_id",
                                    "pkDocumentId",
                                  ),
                                ],
                                [
                                  "Document Type",
                                  getVendorField(
                                    document,
                                    "document_type",
                                    "documentType",
                                  ),
                                ],
                                [
                                  "Document Number",
                                  getVendorField(
                                    document,
                                    "document_number",
                                    "documentNumber",
                                  ),
                                ],
                                [
                                  "Verification Status",
                                  formatDisplayValue(
                                    getVendorField(
                                      document,
                                      "document_verification_status",
                                      "documentVerificationStatus",
                                    ),
                                  ),
                                ],
                                [
                                  "Verified By",
                                  getVendorField(
                                    document,
                                    "verified_by",
                                    "verifiedBy",
                                  ),
                                ],
                                [
                                  "Verified At",
                                  getVendorField(
                                    document,
                                    "verified_at",
                                    "verifiedAt",
                                  ),
                                ],
                                [
                                  "Rejection Reason",
                                  getVendorField(
                                    document,
                                    "rejection_reason",
                                    "rejectionReason",
                                  ),
                                ],
                                [
                                  "Document Created At",
                                  getVendorField(
                                    document,
                                    "document_created_at",
                                    "documentCreatedAt",
                                  ),
                                ],
                              ].map(([label, value]) => (
                                <div
                                  key={`${documentIndex}-${label}`}
                                  className="rounded-lg bg-gray-50 p-3"
                                >
                                  <p className="text-[11px] font-medium uppercase tracking-wide text-gray-400">
                                    {label}
                                  </p>
                                  <p className="mt-1 break-words text-sm font-semibold text-gray-800">
                                    {value || "-"}
                                  </p>
                                </div>
                              ))}

                              <div className="rounded-lg bg-gray-50 p-3 sm:col-span-2 lg:col-span-3">
                                <p className="text-[11px] font-medium uppercase tracking-wide text-gray-400">
                                  Document URL
                                </p>

                                {documentUrl ? (
                                  <div className="mt-2 flex flex-wrap items-center gap-2">
                                    <button
                                      type="button"
                                      onClick={() => {
                                        setPdfPreviewUrl(documentUrl);
                                      }}
                                      className="inline-flex items-center gap-2 rounded-lg bg-violet-600 px-3.5 py-2 text-xs font-semibold text-white transition hover:bg-violet-700"
                                    >
                                      <Eye size={14} />
                                      View PDF
                                    </button>

                                    <span className="max-w-full truncate text-xs text-gray-500 sm:max-w-[520px]">
                                      {documentUrl}
                                    </span>
                                  </div>
                                ) : (
                                  <p className="mt-1 text-sm font-semibold text-gray-800">
                                    -
                                  </p>
                                )}
                              </div>
                            </div>
                          </div>
                        );
                      })}
                    </div>
                  ) : (
                    <div className="rounded-xl border border-dashed border-gray-300 bg-gray-50 p-4 text-sm font-medium text-gray-500">
                      No document details available.
                    </div>
                  )}
                </div>
              </div>
            </div>
          </div>
        </div>
      )}

      {pdfPreviewUrl && (
        <div
          className="fixed inset-0 z-[160] flex items-center justify-center bg-gray-300 p-4"
          onClick={() => setPdfPreviewUrl("")}
        >
          <div
            className="flex h-[85vh] w-full max-w-4xl min-h-0 flex-col overflow-hidden rounded-2xl bg-gray-200"
            onClick={(event) => event.stopPropagation()}
          >
            <div className="flex flex-shrink-0 items-center justify-between bg-gray-200 px-5 py-3">
              <h3 className="text-sm font-semibold text-gray-900">
                PDF Preview
              </h3>

              <button
                type="button"
                onClick={() => setPdfPreviewUrl("")}
                className="flex h-9 w-9 items-center justify-center rounded-lg text-gray-600 transition hover:bg-gray-300 hover:text-gray-900"
                title="Close PDF"
                aria-label="Close PDF"
              >
                <X size={20} />
              </button>
            </div>

            <div className="relative min-h-0 flex-1 overflow-hidden bg-gray-200">
              <iframe
                src={`${pdfPreviewUrl}#toolbar=0&navpanes=0&scrollbar=0&view=FitH`}
                title="PDF Preview"
                frameBorder="0"
                className="absolute bg-gray-200"
                style={{
                  top: "-10px",
                  left: "-24px",
                  width: "calc(100% + 48px)",
                  height: "calc(100% + 20px)",
                  border: 0,
                  outline: 0,
                  boxShadow: "none",
                  margin: 0,
                  padding: 0,
                }}
              />
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default VendorSection;
